import {Component, HostListener, OnInit} from '@angular/core';
import {distinctUntilChanged} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {TemplatesDeleteComponent} from './templates-delete/templates-delete.component';
import {ClipboardService} from 'ngx-clipboard';
import {Individual, InputTypeEnum, Pageable, Tag, Template} from 'src/app/models';
import {ButtonConfig} from 'src/app/components';
import {DataService, IndividualsService, SnackbarService, TemplateService} from 'src/app/services';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {TagInfoPopupComponent} from './tagPopup/tag-info-popup.component';
import {FaqComponent} from 'src/app/screens';
import {stringCompare} from '../../../utilities/string';
import {HttpParams} from '@angular/common/http';
import {NgZone} from '@angular/core';

@Component({
    selector: 'app-template', templateUrl: './templates.component.html', styleUrls: ['./templates.component.scss']
})

export class TemplatesComponent implements OnInit {
    autocompleteTemplate: Template[] = [];
    autocompleteTemplateFormatted: string[] = [];
    isMobileResolution: boolean;
    templates: Template[];
    selectedTemplate: Template;
    templateChange = new Subject<string>();
    searchVar = '';
    admin: boolean;
    selectedDeleteTemplate = false;
    allowEditAccess = false;
    loading = false;
    buttonsDisabled = false;
    processing = false;
    buttonConfig: ButtonConfig;
    createdBy: string;
    currentTemplateOwner: string;
    showSpinner = true;
    parseIterations = 0;
    currentUser: Individual;

    //Grid component inputs
    header = ['ID', 'Name'];
    width = [50, 9999]
    userCreatedTemplatesSetting = true;
    showGridFilter = false;
    page = {
        limit: 20,
        count: 0,
        offset: 0,
        orderBy: 'name',
        orderDir: 'asc'
    };
    oldPage = 0;

    prevTags: Tag[] = [];

    params = new HttpParams();

    // Tags Must All Lower Case
    customTags: string[][] = [['{{applications}}', 'computer'], ['{{clients}}', 'groups'], ['{{confirm}}', 'check'], ['{{date}}', 'date_range'], ['{{sentby}}', 'person']];

    tagPattern = new RegExp(/{{[^{}]*}}/g);
    bracketsPattern = new RegExp(/([{}])/g);
    confirmNewlineRegex = new RegExp(/(\n|\r)\s*{{confirm}}\s*(\n|\r)/g);

    templateForm = new FormGroup({
        name: new FormControl('', [Validators.required, Validators.maxLength(50)]),
        instructions: new FormControl('', [Validators.maxLength(2048)]),
        text: new FormControl('', [Validators.required, Validators.maxLength(2048)]),
        owner: new FormControl('')
    });

    constructor(public ngZone: NgZone, public templateService: TemplateService, public dataService: DataService, private snackbarService: SnackbarService, private dialog: MatDialog, private clipboardService: ClipboardService, private individualsService: IndividualsService) {}

    get templateName(): FormControl {
        return this.templateForm.controls.name as FormControl;
    }

    get templateInstructions(): FormControl {
        return this.templateForm.controls.instructions as FormControl;
    }

    get templateText(): FormControl {
        return this.templateForm.controls.text as FormControl;
    }

    get templateOwner(): FormControl {
        return this.templateForm.controls.owner as FormControl;
    }

    populateForm() {
        this.templateForm.patchValue({
            name: this.selectedTemplate.name,
            instructions: this.selectedTemplate.instructions,
            text: this.selectedTemplate.template_text,
            owner: this.currentTemplateOwner
        });
    }

    resetForm() {
        if(this.templateForm) {
            this.templateForm.reset();
        }
    }

    ngOnInit() {
        this.buttonConfig = {
            enable: true, icon: 'add_circle', label: 'Add Icon', color: 'primary', id: 'TemplateAdd', class: 'flex',
        };

        // spinner disappears only if we can load the user info otherwise we throw an error.
        this.dataService.getUserInfo().subscribe({
            next: user => {
                this.admin = user.isAdmin;
                this.individualsService.getIndividuals(user['email']).subscribe(res => {
                    this.currentUser = res[0];
                }).add(() => this.getTemplatesForGrid());
                this.showSpinner = false;
            }, error: () => {
                this.snackbarService.openSnackBar('Add template is disabled due to an error retrieving user information', true);
                this.showSpinner = false;
            }
        });

        this.templateChange.pipe(distinctUntilChanged()).subscribe({
            next: (term: string) => {
                this.searchVar = term;
                if (term != null && term.trim().length > 1) {
                    this.templateService.getTemplate(term).subscribe(template => {
                        this.autocompleteTemplate = template.content;
                        this.autocompleteTemplateFormatted = [];
                        this.autocompleteTemplate.forEach(e => {
                            this.autocompleteTemplateFormatted.push(e.name);
                        });
                    });
                } else {
                    this.autocompleteTemplate = [];
                    this.autocompleteTemplateFormatted = [];
                }
            }, error: () => {
                this.snackbarService.openSnackBar('Failed to load template search.  Please try again and if problem persists contact an app admin.', true);
            }
        });
    }

    isEditable() {
        return this.admin || this.allowEditAccess;
    }

    @HostListener('window:resize', ['$event']) onResize() {
        this.isMobileResolution = window.innerWidth < 768;
    }

    saveFormData(): void {
        if (this.templateName.valid && this.templateText.valid) {
            this.autocompleteTemplateFormatted = [];
            // add to database
            const createTemplate = new Template(this.selectedTemplate.id, this.templateName.value, this.templateInstructions.value, this.templateText.value, this.currentUser.id.toString(), this.selectedTemplate.is_alert_manager, this.selectedTemplate.is_debug, this.selectedTemplate.tags);

            this.templateService.getTemplate(createTemplate.name).subscribe({
                next: page => {
                    if (this.selectedTemplate?.name !== createTemplate.name && page.content.some(template => stringCompare(template.name, createTemplate.name))) {
                        this.snackbarService.openSnackBar('Cannot create template with duplicate name', true);
                        this.processing = false;
                        return;
                    }
                    if (!createTemplate.id) {
                        this.templateService.createTemplate(createTemplate).subscribe({
                            next: () => this.successfulUpdate(true),
                            error: err => this.handle400(err)
                        }).add(() => this.processing = false);
                    } else {
                        this.templateService.updateTemplate(createTemplate).subscribe({
                            next: () => this.successfulUpdate(false),
                            error: err => this.handle400(err)
                        }).add(() => this.processing = false);
                    }
                }, error: () => {
                    this.snackbarService.openSnackBar('Error saving template, try again later', true);
                    this.processing = false;
                }
            });
        }
    }

    handle400(err) {
        if (err.status === 400) {
            this.snackbarService.openSnackBar('Template not created - Bad Request', true);
            this.buttonsDisabled = false;
        }
    }

    successfulUpdate(created: boolean) {
        this.getTemplatesForGrid();
        this.snackbarService.openSnackBar(created ? 'Template created successfully' : 'Template updated successfully');
        this.selectedTemplate = undefined;
        this.buttonsDisabled = false;
        this.clearAll();
    }

    deleteTemplate(id): void {
        const dialogRef = this.dialog.open(TemplatesDeleteComponent, {
            width: '325px', height: '205px', data: {
                dataKey: this.selectedTemplate.name
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.templateService.deleteTemplate(id).subscribe(() => {
                    this.clearAll();
                    this.snackbarService.openSnackBar('Template deleted successfully');
                    this.getTemplatesForGrid();
                    this.selectedTemplate = undefined;
                });
                this.clearAll();
                this.selectedTemplate = undefined;
                this.clearAll();
                this.buttonsDisabled = false;
            }
        });
    }

    closeTemplate() {
        this.clearAll();
    }

    newTemplate() {
        if (this.currentUser) {
            this.allowEditAccess = true;
            this.selectedDeleteTemplate = false;
            this.templateName.setValue(this.searchVar != null ? this.searchVar : '');
            this.templateInstructions.setValue('');
            this.templateOwner.setValue(this.currentUser.full_name);
            this.templateText.reset();
            this.prevTags = [];
            this.selectedTemplate = new Template(null, null, (this.searchVar != null ? this.searchVar : null), '', this.currentUser.id.toString(), false, false);
            this.searchVar = null;
        } else {
            this.snackbarService.openSnackBar('Please register on the User Profile page before creating a template.', true);
        }
    }

    onTemplateChanged(event) {
        if (event.data === undefined) {
            this.templateChange.next(event);
        }
    }

    selectTemplate(event: MatAutocompleteSelectedEvent) {
        this.openTemplate(this.autocompleteTemplate.find(e => e.name === event.option.value));
    }

    onActivate(event) {
        if (event.type == 'click') {
            this.openTemplate(event.row);
        }
    }

    openTemplate(template: Template) {
        this.loading = true;

        this.clearAll();

        this.selectedDeleteTemplate = true;

        this.selectedTemplate = template;

        this.allowEditAccess = (this.selectedTemplate.owner_id?.toLowerCase() === this.currentUser.id.toString().toLowerCase()) || false;

        this.currentTemplateOwner = null;

        // Only call API to get owner if owner exists
        if (this.selectedTemplate.owner_id) {
            this.individualsService.getIndividual(this.selectedTemplate.owner_id).subscribe({
                next: owner => {
                    this.currentTemplateOwner = owner.full_name;
                    this.patch();
                },
                error: () => {
                    this.patch();
                }
            });
        } else {
            this.patch();
        }
    }

    patch() {
        this.populateForm();
        this.parseMessage();
        this.loading = false;
    }

    clearAll() {
        this.resetForm();
        this.selectedTemplate = undefined;
        this.autocompleteTemplate = [];
        this.autocompleteTemplateFormatted = [];
        this.prevTags = [];
        this.onTemplateChanged('');
    }

    save() {
        this.processing = true;
        this.templateName.markAsTouched();
        this.templateInstructions.markAsTouched();
        this.templateText.markAsTouched();

        if (!this.templateName.valid) {
            this.snackbarService.openSnackBar('Invalid template name', true);
            this.processing = false;
            return;
        } else if (this.templateName.value && this.templateName.value.trim().length < 4) {
            this.snackbarService.openSnackBar('Template name must be at least 4 characters', true);
            this.processing = false;
            this.templateName.setValue(this.templateName.value.trim());
            return;
        }

        if (!this.templateText.valid) {
            this.snackbarService.openSnackBar('Invalid template text', true);
            this.processing = false;
            return;
        }

        if (!this.selectedTemplate.is_alert_manager && this.selectedTemplate.tags.length <= 0) {
            this.snackbarService.openSnackBar('A template must have at least one tag', true);
            this.processing = false;
            return;
        }

        if (this.selectedTemplate.is_alert_manager) { // test for unexpected AlertManager tags
            let terms: string[] = this.templateText.value.match(this.tagPattern) || [];
            terms.forEach((term, i) => terms[i] = term.replace(this.bracketsPattern, ''));
            terms = terms.filter((term) => !['version', 'groupKey', 'truncatedAlerts', 'status', 'receiver', 'externalURL'].includes(term)); // remove any predefined terms
            terms = terms.filter((term) => !term.startsWith('alert.')); // remove any tags that start with alert
            terms = terms.filter((term) => !term.startsWith('groupLabels.')); // remove any tags that start with groupLabels
            terms = terms.filter((term) => !term.startsWith('commonLabels.')); // remove any tags that start with alert
            terms = terms.filter((term) => !term.startsWith('commonAnnotations.')); // remove any tags that start with alert
            terms = terms.filter(this.filterOutHandlebars);
            if (terms.length !== 0) { // if any unaccepted tags found
                this.snackbarService.openSnackBar('Invalid AlertManager tag(s) found: ' + terms, true);
                this.processing = false;
                return;
            }
        }

        this.saveFormData();
    }

    filterOutHandlebars(element: string, index, array): boolean {
        if (element.startsWith('lookup') || element.startsWith('log')) {
            return false;
        }
        if (element.startsWith('#if')) {
            let closingIndex = array.indexOf(`/if`);
            if (closingIndex === -1) {
                return true;
            }
            array.splice(closingIndex, 1); // remove closing tag
            return false;
        }
        if (element.startsWith('#unless')) {
            let closingIndex = array.indexOf(`/unless`);
            if (closingIndex === -1) {
                return true;
            }
            array.splice(closingIndex, 1); // remove closing tag
            return false;
        }
        if (element.startsWith('#with')) {
            let closingIndex = array.indexOf(`/with`);
            if (closingIndex === -1) {
                return true;
            }
            array.splice(closingIndex, 1); // remove closing tag
            return false;
        }
        if (element.startsWith('#each')) {
            let closingIndex = array.indexOf(`/each`);
            if (closingIndex === -1) {
                return true;
            }
            let elseIndex = array.indexOf('else');
            if (elseIndex !== -1 && (elseIndex > index || elseIndex < closingIndex)) {
                array.splice(elseIndex, 1);
            } // remove 'else' tag
            let thisIndex = array.indexOf('this');
            if (thisIndex !== -1 && (thisIndex > index || thisIndex < closingIndex)) {
                array.splice(thisIndex, 1);
            } // remove 'this' tag
            let indexIndex = array.indexOf('@index');
            if (indexIndex !== -1 && (indexIndex > index || indexIndex < closingIndex)) {
                array.splice(indexIndex, 1);
            } // remove '@index' tag
            let keyIndex = array.indexOf('@key');
            if (keyIndex !== -1 && (keyIndex > index || keyIndex < closingIndex)) {
                array.splice(keyIndex, 1);
            } // remove '@key' tag
            array.splice(closingIndex, 1); // remove closing tag
            return false;
        }
    }

    copy(toBeCopied: string) {
        this.clipboardService.copyFromContent(toBeCopied);
        this.snackbarService.openSnackBar(`Tag ${toBeCopied} copied`);
    }

    openTagPopup(tag: Tag, index: number) {
        if (this.isConfirmTag(tag)) {
            this.snackbarService.openSnackBar(`Tag ${tag.name} has no options to change`);
        } else {
            if (this.admin || this.allowEditAccess && !this.loading) {
                this.buttonsDisabled = true;
                let tagInfoPopup = this.dialog.open(TagInfoPopupComponent, {
                    width: '65%', maxHeight: '90%', data: tag
                });

                tagInfoPopup.afterClosed().subscribe((res) => {
                    if (res) {
                        this.selectedTemplate.tags[index] = res;
                    }
                    this.buttonsDisabled = false;
                    this.checkForRenamedTag();
                });
            }
        }
    }

    parseMessage() {
        this.parseIterations = this.parseIterations + 1;

        if (this.selectedTemplate.is_alert_manager) { // don't parse tags if alertManager template
            this.selectedTemplate.tags = [];
            return;
        }

        let tagNames = this.templateText.value.match(this.tagPattern) || [];
        tagNames = tagNames.filter((tagName, i) => tagNames.indexOf(tagName) === i); //removes duplicates
        tagNames.forEach((tagName, i) => {
            if (tagName.length > 54) {
                this.templateText.setValue(this.templateText.value.replaceAll(tagName, tagName.slice(0, 52) + '}}'));
                tagNames[i] = tagName.slice(0, 52).replace(this.bracketsPattern, '');
                this.snackbarService.openSnackBar('Tag names can only be up to 50 characters long', true);
            } else if (tagName.length < 6) { // two characters plus {{}}
                let currentIteration = this.parseIterations;
                setTimeout(() => {
                    if (this.parseIterations === currentIteration) {
                        this.templateText.setValue(this.templateText.value.replaceAll(tagName, ''));
                        tagNames[i] = null; // mark tag for delete
                        this.snackbarService.openSnackBar('Tag names must be 2 characters or longer', true);
                    }
                }, 2000);
            } else {
                tagNames[i] = tagName.replace(this.bracketsPattern, '');
            }
        });
        tagNames = tagNames.filter((tagName) => tagName != null); // remove null tags
        if (tagNames === []) {
            this.selectedTemplate.tags = [];
            this.buttonsDisabled = false;
        } else {
            if (this.prevTags == []) {
                this.prevTags = this.selectedTemplate.tags;
            } else {
                this.selectedTemplate.tags.forEach(tag => {
                    const index = this.prevTags.findIndex(prevTag => prevTag.name === tag.name);
                    if (index == -1) {
                        this.prevTags.push(tag);
                    } else {
                        this.prevTags[index] = tag;
                    }
                });
            }
            this.selectedTemplate.tags = [];
            tagNames.forEach(term => {
                const currTag = this.prevTags.find(tag => tag.name === term);
                if (currTag === null || currTag === undefined) {
                    this.selectedTemplate.tags.push(new Tag(null, this.selectedTemplate.id, term, null, true, true, '', '', this.templateService.getInputType(term)));
                } else {
                    this.selectedTemplate.tags.push(currTag);
                }
            });
            this.selectedTemplate.tags = this.selectedTemplate.tags.filter(tag => tagNames.includes(tag.name)).sort((tagA, tagB) => tagNames.findIndex(indexTag => indexTag === tagA.name) - tagNames.findIndex(indexTag => indexTag === tagB.name));
            this.buttonsDisabled = false;
        }
        // removes confirm tag newline
        this.templateText.setValue(this.templateText.value.replace(this.confirmNewlineRegex, "{{confirm}}\n"));
    }

    checkForRenamedTag() {
        let tagNames = this.templateText.value.match(this.tagPattern);
        tagNames = tagNames.filter((item, index) => tagNames.indexOf(item) === index);
        for(let i = 0; i < tagNames.length; i++) {
            if("{{" + this.selectedTemplate.tags[i].name + "}}" != tagNames[i]) {
                var replaceRegex = new RegExp(tagNames[i],"g");
                this.templateText.setValue(this.templateText.value.replace(replaceRegex, "{{" + this.selectedTemplate.tags[i].name + "}}"));
            }
        }
    }

    isCustom(tag: Tag): boolean {
        return [InputTypeEnum.DATE, InputTypeEnum.CONFIRM, InputTypeEnum.APPLICATIONS, InputTypeEnum.CLIENTS, InputTypeEnum.SENTBY].includes(tag.input_type);
    }

    isConfirmTag(tag: Tag): boolean {
        return tag.input_type === InputTypeEnum.CONFIRM;
    }

    openCustomTagsFAQ() {
        this.dialog.open(FaqComponent, {
            width: '95%',
            height: '95%',
            data: {
                category: 'Templates',
                question: 'What custom tags are supported?'
            }
        });
    }

    openTemplatesFAQ() {
        this.dialog.open(FaqComponent, {
            width: '95%',
            height: '95%',
            data: {
                category: 'Templates',
                question: 'What are the advanced features of templates?'
            }
        });
    }

    summarize(text: string) {
        if (text !== undefined) {
            const textOptions = text.split(',');
            if (textOptions.length > 1) {
                let retString = textOptions[0], i = 1;
                while (i < textOptions.length && retString.length < 25) {
                    if ((retString + ', ' + textOptions[i]).length > 25) {
                        break;
                    }
                    retString = retString + ', ' + textOptions[i];
                    i++;
                }
                return i < textOptions.length ? (retString + ', + ' + (textOptions.length - i) + ' more') : retString;
            } else {
                return text.length < 30 ? text : text.substring(0, 30) + '...';
            }
        }
    }

    summarizeTooltip(text: string): string {
        return this.summarize(text) === text ? undefined : text;
    }

    getErrorText(text: string) {
        if (text) {
            let trimmedText = text.trim();
            if (trimmedText.length == 0) {
                return 'required';
            } else if (trimmedText.length < 4) {
                return 'too short';
            }
        }
        return 'invalid text';
    }

    checkLength(text: FormControl, length: number) {
        if (text) {
            if (text.value.length > length) {
                this.snackbarService.openSnackBar('Max length reached', true);
                text.setValue(text.value.slice(0, length));
            }
        }
    }

    filterChange(value: boolean) {
        this.userCreatedTemplatesSetting = value;
        let old = this.oldPage;
        this.oldPage = this.page.offset;
        this.page.offset = old;
        this.getTemplatesForGrid();
    }

    getTemplatesForGrid() {
        if (this.currentUser) {
            this.templateService.getTemplatesWithIdentifierPage(this.currentUser.id.toString(), this.page).subscribe((res: Pageable<Template>) => {
                if (res.total_elements === 0) {
                    this.userCreatedTemplatesSetting = false;
                    this.showGridFilter = false;
                } else {
                    this.showGridFilter = true;

                    if (this.userCreatedTemplatesSetting) {
                        this.page.count = res.total_elements;
                        this.templates = res.content;
                    }
                }
            }).add(() => {
                if (!this.userCreatedTemplatesSetting) {
                    this.getAllTemplatesPage();
                }
            });
        } else {
            this.userCreatedTemplatesSetting = false;
            this.showGridFilter = false;
            this.getAllTemplatesPage();
        }
    }

    getAllTemplatesPage() {
        this.templateService.getAllTemplatesPage(this.page).subscribe((res: Pageable<Template>) => {
            this.page.count = res.total_elements;
            this.templates = res.content;
        });
    }

    pageCallback(pageInfo: { count?: number, pageSize?: number, limit?: number, offset?: number }) {
        this.page.offset = pageInfo.offset;
        this.getTemplatesForGrid();
    }

    sortCallback(sortInfo: { sorts: { dir: string, prop: string }[], column: {}, prevValue: string, newValue: string }) {
        this.page.orderDir = sortInfo.sorts[0].dir;
        this.page.orderBy = sortInfo.sorts[0].prop;
        this.getTemplatesForGrid();
    }

    openTemplateEditFAQ() {
        this.dialog.open(FaqComponent, {
            width: '95%',
            height: '95%',
            data: {
                category: 'Templates',
                question: 'How do I create, update, or delete a template?'
            }
        });
    }
}
